import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  OnDestroy,
  OnInit,
}                                 from '@angular/core';
import {
  NonNullableFormBuilder,
  Validators,
}                                 from '@angular/forms';
import { MAT_DIALOG_DATA }        from '@angular/material/dialog';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  defer,
  distinctUntilChanged,
  map,
  startWith,
  Subject,
  takeUntil,
}                                 from 'rxjs';
import {
  Feature,
  fileIcon,
  needsBrandColor,
}                                 from '~domain/enums';
import {
  PaymentData,
  PaymentFormData,
  RbDocument,
  SimpleClient,
  UserSettings,
}                                 from '~domain/types';
import {
  DialogButton,
  DialogButtonConfig,
  DialogConfig,
}                                 from '~shared/components';
import { RbDialogForm }           from '~shared/directives';
import { DatePipe }               from '~shared/pipes';
import { FeatureService }         from '../../core';
import { ValidatorService }       from '../../core/validators';
import { MainService }            from '../../main/main.service';
import {
  duplicatePaymentWarningKey,
  duplicatePaymentWarningTranslateArgs,
  getDuplicatePaymentCheckValue,
  sameDuplicatePaymentCheck,
}                                 from '../../payment/duplicate-payment.utils';
import { EditBeneficiaryRequest } from '../../payment/edit-beneficiary.component';
import { DeleteDocumentService }  from '../delete-document/delete-document.service';
import { SelectClientFormData }   from '../select-client/select-client.component';
import { ProcessDocumentService } from './process-document.service';

export type ProcessDocumentRequest = {
  document: RbDocument,
  paymentFormData?: PaymentFormData,
  queuedDocuments: RbDocument[],
  predictedClient?: SimpleClient,
  userSettings: UserSettings,
}

@Component({
  selector: 'rb-process-document-dialog',
  templateUrl: './process-document-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
  providers: [DatePipe],
})
export class ProcessDocumentDialogComponent extends RbDialogForm implements OnInit, OnDestroy {
  paymentFeatureEnabled$ = this.featureService.officeHasFeature(Feature.ISABEL_PI);
  processDocumentType$ = this.processDocumentService.processDocumentType$;
  processDocumentUrl$ = this.processDocumentService.processDocumentUrl$;
  paymentClients$ = this.processDocumentService.paymentClients$;
  paymentAccounts$ = this.processDocumentService.paymentAccounts$;
  beneficiaries$ = combineLatest([
    this.processDocumentService.globalBeneficiaries$,
    this.processDocumentService.clientBeneficiaries$,
  ]).pipe(map(([globalBeneficiaries, clientBeneficiaries]) => globalBeneficiaries.concat(clientBeneficiaries)));
  beneficiariesLoading$ = combineLatest([
    this.processDocumentService.globalBeneficiariesLoading$,
    this.processDocumentService.clientBeneficiariesLoading$,
  ]).pipe(map(([globalBeneficiariesLoading, clientBeneficiariesLoading]) => globalBeneficiariesLoading || clientBeneficiariesLoading));
  beneficiaryEditing$ = this.processDocumentService.paymentBeneficiaryEditing$;
  addPaymentLoading$ = this.processDocumentService.paymentSaving$;
  override form = this.fb.group({
    mode: this.fb.control<'pay' | 'assign' | 'delete'>(this.request.paymentFormData ? 'pay' : 'assign', [Validators.required]),
    client: this.fb.control<SelectClientFormData>({ clientId: this.request.predictedClient?.id ?? '' }),
    payment: this.fb.control<PaymentFormData | null>(this.request.paymentFormData ?? null),
  });
  duplicatePaymentWarningKey$ = combineLatest([
    this.form.controls.mode.valueChanges.pipe(startWith(this.form.controls.mode.value)),
    this.processDocumentService.duplicatePaymentsType$,
  ]).pipe(
    map(([mode, type]) => mode === 'pay' ? duplicatePaymentWarningKey(type) : null),
  );
  duplicatePaymentWarningTranslateArgs$ = combineLatest([
    this.form.controls.mode.valueChanges.pipe(startWith(this.form.controls.mode.value)),
    this.processDocumentService.duplicatePaymentsLatestDate$,
  ]).pipe(
    map(([mode, latestDate]) => mode === 'pay' ? duplicatePaymentWarningTranslateArgs(this.datePipe, latestDate) : null),
  );
  assignDocumentClients$ = this.processDocumentService.assignDocumentClients$;
  assignDocumentIsLoading$ = this.processDocumentService.assignDocumentIsLoading$;
  nextDocumentLoading$ = this.processDocumentService.fetchingDocumentProcessingData$;
  deleteDocumentIsLoading$ = this.deleteDocumentService.deleteDocumentIsLoading$;
  lastUsedButton$ = new BehaviorSubject<DialogButton | null>(null);
  nextButtonIsLoading$ = combineLatest([
    this.nextDocumentLoading$,
    this.lastUsedButton$,
  ]).pipe(
    map(([nextDocumentLoading, lastUsedButton]) =>
      nextDocumentLoading && (lastUsedButton === DialogButton.Next),
    ),
  );
  okButtonIsLoading$ = combineLatest([
    this.addPaymentLoading$,
    this.assignDocumentIsLoading$,
    this.deleteDocumentIsLoading$,
    this.nextDocumentLoading$,
    this.lastUsedButton$,
  ]).pipe(
    map(([addPaymentLoading, assignDocumentIsLoading, deleteDocumentIsLoading, nextDocumentLoading, lastUsedButton]) =>
      (addPaymentLoading || assignDocumentIsLoading || deleteDocumentIsLoading || nextDocumentLoading) && (lastUsedButton === DialogButton.OK),
    ),
  );
  buttons: DialogButtonConfig[] = [
    this.request.queuedDocuments.length === 0
      ? {
        id: DialogButton.Cancel,
        label: 'APP.BUTTON.CANCEL.LABEL',
        icon: 'cross',
        style: 'secondary',
      }
      : {
        id: DialogButton.Next,
        label: 'APP.BUTTON.SKIP.LABEL',
        icon: 'chevron-right',
        style: 'secondary',
        autoClose: false,
        isLoading$: this.nextButtonIsLoading$,
      },
    {
      id: DialogButton.OK,
      label: 'APP.BUTTON.CONFIRM.LABEL',
      icon: 'check',
      style: 'primary',
      autoClose: false,
      disabled$: this.submitDisabled$,
      isLoading$: this.okButtonIsLoading$,
    },
  ];
  dialogConfig: DialogConfig = {
    title: 'APP.PROCESS_DOCUMENT_DIALOG.TITLE',
    beta: true,
    closeable: true,
    maximizable: true,
    errorKey$: this.errorKey$,
    errorContext: 'APP.PROCESS_DOCUMENT_DIALOG.ERROR',
    warningKey$: this.duplicatePaymentWarningKey$,
    warningContext: 'APP.ADD_PAYMENT_DIALOG.WARNING',
    warningTranslateArgs$: this.duplicatePaymentWarningTranslateArgs$,
    queuedItems: this.request.queuedDocuments?.length,
    buttons: this.buttons,
  };
  dialogMaximized: boolean = false;
  protected readonly needsBrandColor = needsBrandColor;
  protected readonly fileIcon = fileIcon;
  private readonly documentNameEditingErrorKey = 'document_name_editing';
  private readonly documentNameEditing$ = new BehaviorSubject(false);
  private destroy$ = new Subject<void>();

  constructor(
    protected override readonly fb: NonNullableFormBuilder,
    protected override readonly validatorService: ValidatorService,
    private readonly datePipe: DatePipe,
    private readonly featureService: FeatureService,
    private readonly deleteDocumentService: DeleteDocumentService,
    private readonly mainService: MainService,
    private readonly processDocumentService: ProcessDocumentService,
    @Inject(MAT_DIALOG_DATA) public request: ProcessDocumentRequest,
  ) {
    super(fb, validatorService);
  }

  get assignFormRadioGroupHeight() {
    return this.dialogMaximized ? 'calc(100vh - 36.9rem)' : 'min(51.2rem, calc(100vh - 36.9rem))';
  }

  get paymentFormHeight() {
    return this.dialogMaximized ? 'calc(100vh - 29.4rem)' : 'min(58.9rem, calc(100vh - 29.4rem))';
  }

  get docViewerMaxHeight() {
    return this.dialogMaximized ? 'calc(100vh - 18.2rem)' : 'min(70.1rem, calc(100vh - 18.2rem))';
  }

  override get errorKey$() {
    return combineLatest([this.formStatus$, defer(() => this.documentNameEditing$)]).pipe(
      map(([, documentNameEditing]) => {
        if (this.form.controls.mode.value === 'assign' && this.form.controls.client.errors) {
          return Object.keys(this.form.controls.client.errors)[0];
        }
        if (this.form.controls.mode.value === 'pay' && this.form.controls.payment.errors) {
          return Object.keys(this.form.controls.payment.errors)[0];
        }
        if (documentNameEditing) {
          return this.documentNameEditingErrorKey;
        }
        return null;
      }),
      distinctUntilChanged(),
    );
  }

  override get submitDisabled$() {
    return combineLatest([this.formStatus$, defer(() => this.documentNameEditing$)]).pipe(
      map(([, documentNameEditing]) => {
        if (this.form.controls.mode.value === 'assign') {
          return !this.form.controls.client.valid || documentNameEditing;
        }
        if (this.form.controls.mode.value === 'delete') {
          return documentNameEditing;
        }
        return !this.form.controls.payment.valid || documentNameEditing;
      }),
      distinctUntilChanged(),
    );
  }

  private get documentNameEditing() {
    return this.documentNameEditing$.value;
  }

  ngOnInit() {
    if (this.request.userSettings.dialogMaximized) {
      this.dialogMaximized = true;
    }

    this.form.controls.payment.valueChanges.pipe(
      startWith(this.form.controls.payment.value),
      map((payment) => getDuplicatePaymentCheckValue(payment, { mode: this.form.controls.mode.value })),
      debounceTime(500),
      distinctUntilChanged(sameDuplicatePaymentCheck),
      takeUntil(this.destroy$),
    ).subscribe((value) => {
      if (!value) {
        this.processDocumentService.clearDuplicatePayments();
      } else {
        this.processDocumentService.checkDuplicatePayments(value.clientId, value.request);
      }
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  fetchClientBeneficiaries(clientId: string) {
    this.processDocumentService.fetchClientBeneficiaries(clientId);
  }

  saveBeneficiary(editBeneficiaryRequest: EditBeneficiaryRequest) {
    const { beneficiary, clientId } = editBeneficiaryRequest;
    if (beneficiary.id !== null) {
      if (beneficiary.singleUse) {
        this.processDocumentService.deleteBeneficiary(beneficiary.id, clientId);
      } else {
        this.processDocumentService.updateBeneficiary(beneficiary.id, clientId, beneficiary);
      }
    } else if (!beneficiary.singleUse) {
      this.processDocumentService.addBeneficiary(clientId, beneficiary);
    }
  }

  onDocumentNameRenamingChange(renaming: boolean) {
    this.documentNameEditing$.next(renaming);
    this.form.updateValueAndValidity();
  }

  onDialogButtonClick(dialogButton: DialogButton) {
    if (
      dialogButton === DialogButton.OK
      && !this.documentNameEditing
      && this.form.controls.mode.value === 'pay'
      && this.form.controls.payment.value
      && this.form.controls.payment.valid
    ) {
      this.form.controls.payment.markAllAsTouched();
      const paymentFormData: PaymentData = {
        documentId: this.request.document.id,
        clientId: this.form.controls.payment.value.clientId,
        accountId: this.form.controls.payment.value.accountId,
        remittanceInformation: this.form.controls.payment.value.remittanceInformation,
        internalNote: this.form.controls.payment.value.internalNote,
        remittanceInformationType: this.form.controls.payment.value.remittanceInformationType,
        counterpartName: this.form.controls.payment.value.beneficiary?.counterpartName,
        counterpartReference: this.form.controls.payment.value.beneficiary?.counterpartReference,
        executionDate: this.form.controls.payment.value.executionDate,
        amount: this.form.controls.payment.value.amount!,
      };
      this.lastUsedButton$.next(DialogButton.OK);
      this.processDocumentService.savePayment(paymentFormData, this.request.queuedDocuments);
      this.processDocumentService.clearDuplicatePayments();
    }

    if (
      dialogButton === DialogButton.OK
      && !this.documentNameEditing
      && this.form.controls.mode.value === 'assign'
      && this.form.controls.client.valid
    ) {
      this.lastUsedButton$.next(DialogButton.OK);
      this.processDocumentService.clearDuplicatePayments();
      this.processDocumentService.assignDocument(
        this.request.document.id,
        this.form.controls.client.value.clientId,
        this.request.queuedDocuments,
      );
    }

    if (dialogButton === DialogButton.OK && !this.documentNameEditing && this.form.controls.mode.value === 'delete') {
      this.lastUsedButton$.next(DialogButton.OK);
      this.processDocumentService.clearDuplicatePayments();
      this.deleteDocumentService.deleteDocuments([this.request.document.id], this.request.queuedDocuments);
    }

    if (dialogButton === DialogButton.Next) {
      const qDocs = [...this.request.queuedDocuments];
      const doc = qDocs.shift();
      if (doc) {
        this.lastUsedButton$.next(DialogButton.Next);
        this.processDocumentService.fetchNextDocumentProcessingData(doc, qDocs);
        this.processDocumentService.clearDuplicatePayments();
      }
    }

    if (dialogButton === DialogButton.Cancel) {
      this.onClose();
    }
  }

  onClose() {
    this.processDocumentService.clearProcessDocument();
    this.processDocumentService.clearDuplicatePayments();
    this.processDocumentService.reloadPage();
  }

  onMaximizedChange(newValue: boolean) {
    this.mainService.setUserSettings({ dialogMaximized: newValue });
  }
}
